import { Controller } from "@hotwired/stimulus"

// A clickable count on a post's engagement row ("Ana Silva and 41 others",
// "60 reposts"): opens one shared <dialog> and loads who is behind it
// (EngagedPeopleController). Inside, "Show more" appends the next page,
// LinkedIn's reaction tabs filter the list, and X's Reposts / Quotes swap it.
export default class extends Controller {
    static values = { url: String }

    open(event) {
        event.preventDefault()
        event.stopPropagation()
        const dialog = this._dialog()
        dialog._engagedCard = event.currentTarget.closest(".reflection-card")
        dialog.innerHTML = `<div class="engaged-people engaged-people--loading" role="status" aria-label="Loading">
            ${'<div class="engaged-people-skeleton"></div>'.repeat(4)}</div>`
        if (!dialog.open) dialog.showModal()
        this._load(dialog, this.urlValue)
    }

    _dialog() {
        let dialog = document.getElementById("engaged-people-dialog")
        if (dialog) return dialog

        dialog = document.createElement("dialog")
        dialog.id = "engaged-people-dialog"
        dialog.className = "engaged-people-dialog"
        dialog.setAttribute("aria-labelledby", "engaged_people_title")
        dialog.addEventListener("click", (event) => this._click(dialog, event))
        document.body.appendChild(dialog)
        return dialog
    }

    _click(dialog, event) {
        // The backdrop: a click on the dialog itself, outside its box.
        if (event.target === dialog) {
            const box = dialog.getBoundingClientRect()
            if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close()
            return
        }
        const close = event.target.closest("[data-engaged-people-close]")
        if (close) return dialog.close()

        const retryPanel = event.target.closest("[data-engaged-people-retry-panel]")
        if (retryPanel) return this._load(dialog, retryPanel.dataset.engagedPeopleRetryPanel)

        const inlineSwap = event.target.closest("[data-engaged-people-inline-switch]")
        if (inlineSwap) {
            const host = inlineSwap.closest("[data-engaged-people-inline-url]")
            if (host && !inlineSwap.classList.contains("is-active")) {
                host.dataset.engagedPeopleInlineUrl = inlineSwap.dataset.engagedPeopleInlineSwitch
                this._loadInline(dialog, host)
            }
            return
        }

        const retry = event.target.closest("[data-engaged-people-retry]")
        if (retry) return this._loadInline(dialog, retry.closest("[data-engaged-people-inline-url]"))

        const more = event.target.closest("[data-engaged-people-more]")
        if (more) {
            const listScope = more.closest("[data-engaged-people-inline-url]") || dialog
            more.disabled = true
            more.textContent = "Loading…"
            fetch(more.dataset.engagedPeopleMore, { headers: { Accept: "text/html" }, credentials: "same-origin" })
                .then((response) => (response.ok ? response.text() : Promise.reject(new Error(response.status))))
                .then((html) => {
                    more.closest("li").outerHTML = html
                    this._reactionTabs(listScope)
                })
                .catch(() => { more.disabled = false; more.textContent = "Show more" })
            return
        }

        const swap = event.target.closest("[data-engaged-people-switch]")
        if (swap && !swap.classList.contains("is-active")) return this._load(dialog, swap.dataset.engagedPeopleSwitch)

        const tab = event.target.closest("[data-engaged-people-filter]")
        if (tab) this._filter(dialog, tab)
    }

    _load(dialog, url) {
        const request = (dialog._engagedRequest || 0) + 1
        dialog._engagedRequest = request
        fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin" })
            .then((response) => (response.ok ? response.text() : Promise.reject(new Error(response.status))))
            .then((html) => {
                if (dialog._engagedRequest !== request) return
                dialog.innerHTML = html
                this._reactionTabs(dialog)
                this._syncCounts(dialog)
                dialog.querySelectorAll("[data-engaged-people-inline-url]").forEach((host) => this._loadInline(dialog, host))
            })
            .catch(() => {
                if (dialog._engagedRequest !== request) return
                const existing = dialog.querySelector(".engagement-results")
                if (existing) {
                    const retry = document.createElement("button")
                    retry.type = "button"
                    retry.className = "engaged-people-more"
                    retry.dataset.engagedPeopleRetryPanel = url
                    retry.textContent = "Couldn't load that view. Try again"
                    existing.querySelector("[data-engaged-people-retry-panel]")?.remove()
                    existing.prepend(retry)
                    return
                }
                dialog.innerHTML = `<div class="engaged-people"><header class="engaged-people-head"><h2 class="engaged-people-title" id="engaged_people_title">Couldn't load this</h2>
                    <button class="engaged-people-close" type="button" data-engaged-people-close aria-label="Close"><i class="bi bi-x-lg" aria-hidden="true"></i></button></header>
                    <p class="engaged-people-empty">Try again in a moment.</p></div>`
            })
    }

    async _loadInline(dialog, host) {
        const request = (host._engagedRequest || 0) + 1
        host._engagedRequest = request
        host.setAttribute("aria-busy", "true")
        host.textContent = ""
        const loading = document.createElement("p")
        loading.className = "engaged-people-empty"
        loading.textContent = "Loading people…"
        host.append(loading)
        try {
            const response = await fetch(host.dataset.engagedPeopleInlineUrl, { headers: { Accept: "text/html" }, credentials: "same-origin" })
            if (!response.ok) throw new Error(response.status)
            const html = await response.text()
            if (!host.isConnected || !dialog.contains(host) || host._engagedRequest !== request) return
            host.innerHTML = html
            this._reactionTabs(host)
            this._syncCounts(dialog, host)
        } catch (_error) {
            if (!host.isConnected || !dialog.contains(host) || host._engagedRequest !== request) return
            host.textContent = ""
            const retry = document.createElement("button")
            retry.type = "button"
            retry.className = "engaged-people-more"
            retry.dataset.engagedPeopleRetry = "true"
            retry.textContent = "Couldn't load people. Try again"
            host.append(retry)
        } finally {
            if (host._engagedRequest === request) host.setAttribute("aria-busy", "false")
        }
    }

    // LinkedIn's tabs: All, then one per reaction type, most used first,
    // counted from the people loaded so far.
    _reactionTabs(dialog) {
        const nav = dialog.querySelector("[data-engaged-people-reaction-tabs]")
        if (!nav) return
        const rows = Array.from(dialog.querySelectorAll(".engaged-person[data-reaction]"))
        const types = new Map()
        rows.forEach((row) => {
            const type = row.dataset.reaction
            const badge = row.querySelector(".engaged-person-reaction")
            const entry = types.get(type) || { count: 0, emoji: badge?.textContent || "", name: badge?.title || type }
            entry.count += 1
            types.set(type, entry)
        })
        const active = nav.querySelector(".is-active")?.dataset.engagedPeopleFilter || "all"
        const total = nav.dataset.total || rows.length
        const inPanel = Boolean(nav.closest("[data-engagement-panel]"))
        const complete = !dialog.querySelector("[data-engaged-people-more]") && Number(total) === rows.length
        const tabs = [["all", inPanel ? "All types" : `All ${total}`, "All reactions"]].concat(
            Array.from(types.entries()).sort((a, b) => b[1].count - a[1].count)
                .map(([type, entry]) => [type, `${entry.emoji}${complete ? ` ${entry.count}` : ""}`, entry.name])
        )
        // Built with DOM calls, never markup: the labels come from a network.
        nav.replaceChildren(...tabs.map(([key, label, title]) => {
            const button = document.createElement("button")
            button.type = "button"
            button.className = `engaged-people-tab${key === active ? " is-active" : ""}`
            button.dataset.engagedPeopleFilter = key
            button.title = title
            button.setAttribute("aria-pressed", String(key === active))
            button.textContent = label
            return button
        }))
        nav.hidden = types.size < 2
        const current = nav.querySelector(".is-active")
        if (current) this._filter(dialog, current)
    }

    // Each modal owns a source-count snapshot. Inline responses reconcile it
    // independently, so parallel channel loads cannot overwrite one another.
    _syncCounts(dialog, host = null) {
        const panel = dialog.querySelector("[data-engagement-panel]")
        if (!panel) return
        const counts = JSON.parse(panel.dataset.engagementCounts)
        host?.querySelectorAll("[data-engagement-live-count]").forEach((marker) => {
            const count = Number(marker.dataset.engagementLiveCount)
            if (Number.isSafeInteger(count) && count >= 0) counts[marker.dataset.engagementSource] = count
        })
        panel.dataset.engagementCounts = JSON.stringify(counts)
        const aggregate = Object.values(counts).reduce((sum, count) => sum + Number(count), 0)
        const selected = panel.dataset.engagementChannel
        const value = selected ? counts[selected] : aggregate
        const metric = panel.querySelector('[data-engagement-metric="likes"]')
        if (metric && panel.dataset.engagementAvailable !== "false") {
            metric.querySelector("[data-engagement-value]").textContent = value.toLocaleString()
            const label = metric.querySelector("span")
            const plural = panel.classList.contains("engagement-panel--single") || value !== 1
            label.textContent = label.textContent.startsWith("like") ? (plural ? "likes" : "like") : (plural ? "reactions" : "reaction")
        }
        panel.querySelectorAll('.engagement-channel-count [data-engagement-source][data-engagement-count-kind="likes"]').forEach((node) => {
            node.textContent = Number(counts[node.dataset.engagementSource]).toLocaleString()
        })
        const cardMetric = dialog._engagedCard?.querySelector('.reflection-engagement-summary [data-engagement-metric="likes"]')
        if (cardMetric) {
            cardMetric.querySelector("strong").textContent = aggregate.toLocaleString()
            const label = aggregate === 1 ? "reaction" : "reactions"
            cardMetric.querySelector("span").textContent = ` ${label}`
            cardMetric.setAttribute("aria-label", `${aggregate.toLocaleString()} ${label}: view engagement`)
        }
    }

    _filter(dialog, tab) {
        const key = tab.dataset.engagedPeopleFilter
        tab.parentElement.querySelectorAll("[data-engaged-people-filter]").forEach((button) => {
            const on = button === tab
            button.classList.toggle("is-active", on)
            button.setAttribute("aria-pressed", String(on))
        })
        dialog.querySelectorAll(".engaged-person[data-reaction]").forEach((row) => {
            row.hidden = key !== "all" && row.dataset.reaction !== key
        })
    }
}
